import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AxiosInstance from "@/utils/AxiosInstance";
import { getErrToast } from "@/utils/helpers";
import { cn } from "@/lib/utils";

export type InfiniteScrollSelectOption = {
  value: string;
  label: string;
};

export type InfiniteScrollSelectPage = {
  options: InfiniteScrollSelectOption[];
  total?: number;
};

type QueryValue = string | number | boolean | null | undefined;

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  url: string;
  /** Extra query params. `page` and `limit` are added here. Search can be passed later the same way. */
  params?: Record<string, QueryValue>;
  pageSize?: number;
  enabled?: boolean;
  mapPage: (payload: unknown) => InfiniteScrollSelectPage;
  emptyLabel?: string;
};

function definedParams(params: Record<string, QueryValue> | undefined) {
  const next: Record<string, string | number | boolean> = {};
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    next[key] = value;
  });
  return next;
}

function mergeOptions(current: InfiniteScrollSelectOption[], incoming: InfiniteScrollSelectOption[]) {
  const seen = new Set(current.map((option) => option.value));
  const next = [...current];
  incoming.forEach((option) => {
    if (!option.value || seen.has(option.value)) return;
    seen.add(option.value);
    next.push(option);
  });
  return next;
}

export function InfiniteScrollSingleSelect({
  label,
  value,
  onChange,
  url,
  params,
  pageSize = 20,
  enabled = true,
  mapPage,
  emptyLabel = "No results",
}: Props) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<InfiniteScrollSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selected, setSelected] = useState<InfiniteScrollSelectOption | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<InfiniteScrollSelectOption[]>([]);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const requestGen = useRef(0);
  const mapPageRef = useRef(mapPage);
  const paramsRef = useRef(params);
  mapPageRef.current = mapPage;
  paramsRef.current = params;
  const paramsKey = JSON.stringify(definedParams(params));

  const resetList = useCallback(() => {
    optionsRef.current = [];
    setOptions([]);
    setHasMore(true);
    pageRef.current = 0;
    hasMoreRef.current = true;
    loadingRef.current = false;
    requestGen.current += 1;
  }, []);

  useEffect(() => {
    resetList();
  }, [enabled, paramsKey, resetList, url]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const match = options.find((option) => option.value === value);
    if (match) setSelected(match);
  }, [options, value]);

  const loadNext = useCallback(async () => {
    if (!enabled || !url || loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const nextPage = pageRef.current + 1;
    const requestId = requestGen.current;
    try {
      const { data } = await AxiosInstance().get(url, {
        params: {
          ...definedParams(paramsRef.current),
          page: nextPage,
          limit: pageSize,
          skip: (nextPage - 1) * pageSize,
        },
      });
      if (requestId !== requestGen.current) return;
      const mapped = mapPageRef.current(data);
      const incoming = mapped.options ?? [];
      const merged = mergeOptions(optionsRef.current, incoming);
      const added = merged.length - optionsRef.current.length;
      optionsRef.current = merged;
      setOptions(merged);
      pageRef.current = nextPage;
      const more =
        added > 0 &&
        (typeof mapped.total === "number" ? merged.length < mapped.total : incoming.length >= pageSize);
      hasMoreRef.current = more;
      setHasMore(more);
    } catch (error) {
      hasMoreRef.current = false;
      setHasMore(false);
      getErrToast(error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [enabled, pageSize, paramsKey, url]);

  useEffect(() => {
    if (!open || !enabled || pageRef.current > 0 || !hasMoreRef.current) return;
    void loadNext();
  }, [enabled, loadNext, open, paramsKey, url]);

  useEffect(() => {
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!open || !root || !sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadNext();
      },
      { root, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadNext, open, options.length]);

  const choose = (option: InfiniteScrollSelectOption) => {
    setSelected(option);
    onChange(option.value);
    setOpen(false);
  };

  const clear = () => {
    setSelected(null);
    onChange("");
  };

  const active = !!value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-9 max-w-[220px] rounded-lg px-3 text-sm font-medium",
            active
              ? "border-transparent bg-primary-dark text-white hover:bg-primary hover:text-white"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-700",
          )}
        >
          <span className="truncate">{active ? selected?.label || label : label}</span>
          {active ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Clear ${label}`}
              className="inline-flex"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                clear();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                clear();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 overflow-hidden p-0">
        <div ref={listRef} className="max-h-64 overflow-y-auto p-1.5">
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F3F4F6]",
                  isSelected && "bg-primary/10 font-medium text-primary hover:bg-primary/15",
                )}
                onClick={() => choose(option)}
              >
                <span className="truncate capitalize">{option.label}</span>
                {isSelected ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
              </button>
            );
          })}
          {!loading && options.length === 0 && pageRef.current > 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
          ) : null}
          <div ref={sentinelRef} className="flex justify-center py-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
